import { Controller } from '@hotwired/stimulus';

const DEFAULT_FORMATS = ['qr_code', 'code_128', 'ean_13', 'ean_8', 'upc_a', 'upc_e'];

export default class extends Controller {
  static targets = ['input', 'video', 'status', 'scanButton'];

  connect() {
    this.stream = null;
    this.starting = false;
    this.scanning = false;
    this.detector = null;
    this.zxingControls = null;
  }

  disconnect() {
    this.stop();
  }

  toggleScan() {
    if (this.starting || this.scanning) this.stop();
    else this.start();
  }

  async start() {
    if (this.starting || this.scanning || this.inputTarget.disabled || this.inputTarget.readOnly) {
      return;
    }

    this.starting = true;

    if (!navigator.mediaDevices?.getUserMedia) {
      this.starting = false;
      this.showStatus(window.dynamicFormI18n.camera_not_supported);
      return;
    }

    // Prefer native BarcodeDetector; use ZXing fallback for browsers like iOS Safari.
    if ('BarcodeDetector' in window) {
      return this.startNativeScan();
    }

    return this.startZxingScan();
  }

  beginScan() {
    this.showStatus(window.dynamicFormI18n.scan_camera_starting);
    this.setScanButton(true);
    this.videoTarget.classList.remove('hidden');
  }

  async startNativeScan() {
    try {
      this.beginScan();

      const supportedFormats = await BarcodeDetector.getSupportedFormats();
      if (!this.starting) return;

      const formats = DEFAULT_FORMATS.filter((format) => supportedFormats.includes(format));
      this.detector = new BarcodeDetector({ formats: formats.length > 0 ? formats : undefined });

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      if (!this.starting) {
        stream.getTracks().forEach((track) => track.stop());
        this.starting = false;
        return;
      }

      this.stream = stream;
      this.videoTarget.srcObject = this.stream;
      await this.videoTarget.play();
      if (!this.starting) {
        this.stop();
        return;
      }

      this.starting = false;
      this.scanning = true;
      this.showStatus(window.dynamicFormI18n.scan_point_camera);
      this.scanLoop();
    } catch (error) {
      console.warn('Could not start code scanner', error);
      this.stop();
      this.showStatus(window.dynamicFormI18n.scan_camera_failed);
    }
  }

  async startZxingScan() {
    try {
      this.beginScan();

      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      if (!this.starting) return;

      const reader = new BrowserMultiFormatReader();
      if (!this.starting) return;

      this.starting = false;
      this.scanning = true;
      this.showStatus(window.dynamicFormI18n.scan_point_camera);

      this.zxingControls = await reader.decodeFromVideoDevice(
        undefined,
        this.videoTarget,
        (result, error, controls) => {
          this.zxingControls ??= controls;

          if (!this.scanning) return;

          if (result?.getText) {
            this.applyScanValue(result.getText());
            this.stop();
            return;
          }

          // NotFoundException is expected while searching frames; ignore it.
          if (error?.name && error.name !== 'NotFoundException') {
            console.warn('ZXing scan error', error);
          }
        }
      );

      if (!this.scanning) {
        this.zxingControls?.stop();
        this.zxingControls = null;
      }
    } catch (error) {
      console.warn('Could not start ZXing scanner', error);
      this.stop();
      this.showStatus(window.dynamicFormI18n.scan_camera_failed);
    }
  }

  stop() {
    this.starting = false;
    this.scanning = false;
    this.videoTarget.classList.add('hidden');

    try {
      if (this.videoTarget.srcObject) {
        this.videoTarget.srcObject = null;
      }

      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }

      if (this.zxingControls) {
        try {
          this.zxingControls.stop();
        } catch (error) {
          console.warn('Could not fully stop ZXing scanner', error);
        }
        this.zxingControls = null;
      }
    } catch (error) {
      console.warn('Could not fully stop scanner', error);
    }

    this.setScanButton(false);
  }

  async scanLoop() {
    if (
      !this.scanning ||
      !this.detector ||
      this.videoTarget.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
    ) {
      if (this.scanning) requestAnimationFrame(() => this.scanLoop());
      return;
    }

    try {
      const detections = await this.detector.detect(this.videoTarget);
      const value = detections.find((detection) => detection?.rawValue)?.rawValue;

      if (value) {
        this.applyScanValue(value);
        this.stop();
        return;
      }
    } catch (error) {
      console.warn('Code scan failed', error);
      this.stop();
      this.showStatus(window.dynamicFormI18n.scan_failed_retry);
      return;
    }

    requestAnimationFrame(() => this.scanLoop());
  }

  setScanButton(active) {
    const i18n = window.dynamicFormI18n;
    this.scanButtonTarget.title = i18n[active ? 'stop_scanning' : 'scan_code'];
    this.scanButtonTarget.querySelector('[data-scan-icon]')?.classList.toggle('hidden', active);
    this.scanButtonTarget.querySelector('[data-stop-icon]')?.classList.toggle('hidden', !active);
  }

  showStatus(text, tone = 'neutral') {
    this.statusTarget.textContent = text;
    this.statusTarget.classList.remove('text-gray-600', 'text-greenProspect');
    this.statusTarget.classList.add(tone === 'success' ? 'text-greenProspect' : 'text-gray-600');
    this.statusTarget.classList.remove('hidden');
  }

  applyScanValue(value) {
    this.inputTarget.value = value.trim();
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.showStatus(window.dynamicFormI18n.scan_success, 'success');
  }
}
